/** @jsxImportSource @emotion/react */

import { css } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { isEmpty } from 'lodash';
import type { ChangeEvent } from 'react';
import { V } from '../Layout';
import { MultiSelectChips } from './MultiSelectChips';

const MULTI_SELECT_INPUT_SX: SxProps<Theme> = {
    justifyContent: 'center',
    gap: 0.5,
    px: 1,
    py: 0.5,
    minWidth: 0,
    backgroundColor: 'transparent'
};

interface MultiSelectInputProps {
    selectedValues: unknown[];
    inputValue: string;
    onInputChange: (value: string) => void;
    onRemove: (value: unknown) => void;
    getInputProps: () => object;
    getTagProps?: (params: { index: number }) => object;
    errorMessage: string;
    focused: boolean;
    theme: Theme;
    readOnly?: boolean;
}

export function MultiSelectInput({
    selectedValues,
    inputValue,
    onInputChange,
    onRemove,
    getInputProps,
    getTagProps,
    errorMessage,
    focused,
    theme,
    readOnly = false
}: MultiSelectInputProps) {
    const showErrorClass = !focused && !isEmpty(errorMessage);
    const className = showErrorClass ? 'Mui-error' : '';

    function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
        onInputChange(event.target.value);
    }

    return (
        <V fill sx={MULTI_SELECT_INPUT_SX} className={className}>
            <MultiSelectChips
                selectedValues={selectedValues}
                getTagProps={getTagProps}
                onRemove={onRemove}
                allValuesVisible
                readOnly={readOnly}
            />
            <input
                {...getInputProps()}
                css={css`
                    width: 100%;
                    min-width: 60px;
                    border: none;
                    padding: 0;
                    font-size: inherit;
                    background-color: transparent;
                    color: ${theme.palette.text.primary};
                    outline: none;
                `}
                value={inputValue || ''}
                onChange={handleInputChange}
            />
        </V>
    );
}
